import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Wizard from '../../../wizard/Wizard';
import Button from '../../../button/Button';
import { Paragraph } from '../../../typography';

import UserList from '../startNewInternalChatModal/stepOne/UserList';
import ParticipantsAccessLevels from '../groupAccessLevelSelect/ParticipantsAccessLevels';
import GroupModalTitle from './GroupModalTitle';

import { messageGroupsService } from '../../../../newServices/corporateChat';
import { buildGroupParticipants, getApiErrorMessage } from '../../../../utils/internalChatGroups';

import internalChatToast from '../../utils/internalChatToast';
import useApplyGroupUpdate from '../../utils/useApplyGroupUpdate';

const STEPS = {
    USERS: 0,
    PERMISSIONS: 1
};

const AddGroupParticipantsModal = ({ show, group, onCancel, onAdded }) => {
    const intl = useIntl();
    const applyGroupUpdate = useApplyGroupUpdate();
    const loggedUserId = useSelector(state => state.auth.userData?.id);

    const [currWizardStep, setCurrWizardStep] = useState(STEPS.USERS);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [participantLevels, setParticipantLevels] = useState({});
    const [errorMessage, setErrorMessage] = useState(null);
    const [saving, setSaving] = useState(false);

    /** Quem já participa (e o próprio usuário) não aparece: a rota também troca a
     *  permissão de quem já está, e escolhê-lo de novo sobrescreveria a dele.
     */
    const hiddenUserIds = React.useMemo(() => {
        return [loggedUserId, ...((group?.participants || []).map(participant => participant.userId))];
    }, [group, loggedUserId]);

    const resetWizard = () => {
        setCurrWizardStep(STEPS.USERS);
        setSelectedUsers([]);
        setParticipantLevels({});
        setErrorMessage(null);
    }

    const goToStep = (step) => {
        if (step < currWizardStep) {
            setErrorMessage(null);
            setCurrWizardStep(step);
            return;
        }
        if (currWizardStep === STEPS.USERS && selectedUsers.length === 0) {
            setErrorMessage(intl.formatMessage({ id: "Por favor, selecione pelo menos um usuário." }));
            return;
        }
        setErrorMessage(null);
        setCurrWizardStep(step);
    }

    /** O mesmo usuário aparece uma vez em cada equipe dele: só pode ser escolhido uma vez. */
    const toggleSelectUser = useCallback((user) => {
        if (selectedUsers.find(u => u.id === user.id && u.groupId !== user.groupId)) {
            alert('Usuário já selecionado em outro grupo');
            return;
        }
        setSelectedUsers(prevState => prevState.includes(user) ?
            prevState.filter(item => item !== user) :
            [...prevState, user]);
        setErrorMessage(null);
    }, [selectedUsers]);

    const handleRemoveUser = (user) => {
        setSelectedUsers(prevState => prevState.filter(item => item !== user));
    }

    const handleChangeParticipantLevel = useCallback((userId, level) => {
        setParticipantLevels(prevState => ({
            ...prevState,
            [userId]: level
        }));
    }, []);

    const handleCancel = () => {
        if (saving) return;
        resetWizard();
        onCancel();
    }

    const handleConfirm = async () => {
        setSaving(true);
        try {
            const res = await messageGroupsService.postMessageGroupParticipants(
                group.id,
                buildGroupParticipants(selectedUsers, participantLevels)
            );
            applyGroupUpdate(res && res.data);
            internalChatToast.success(intl.formatMessage({ id: "Participante(s) adicionado(s) com sucesso!" }));
            resetWizard();
            onAdded();
        } catch (error) {
            internalChatToast.error(
                getApiErrorMessage(error) || intl.formatMessage({ id: "Não foi possível adicionar os participantes. Tente novamente." })
            );
        } finally {
            setSaving(false);
        }
    }

    return (
        <Wizard
            show={show}
            title={<GroupModalTitle title={intl.formatMessage({ id: "Adicionar participantes" })} groupName={group?.name} />}
            currStep={currWizardStep}
            errorMessage={errorMessage}
            loading={saving}
            onWizardAbort={handleCancel}
            onClick={goToStep}>
            <Wizard.Step active={currWizardStep === STEPS.USERS} title="Participantes">
                <Wizard.StepContent>
                    <UserList
                        show={show}
                        selectedUsers={selectedUsers}
                        hiddenUserIds={hiddenUserIds}
                        handleRemoveUser={handleRemoveUser}
                        toggleSelectUser={toggleSelectUser}
                        setErrorMessage={setErrorMessage} />
                </Wizard.StepContent>
            </Wizard.Step>
            <Wizard.Step active={currWizardStep === STEPS.PERMISSIONS} title="Permissões">
                <Wizard.StepContent>
                    <div className="p-1">
                        <Paragraph className="mb-50">
                            {intl.formatMessage({ id: "Permissão de cada participante:" })}
                        </Paragraph>
                        <ParticipantsAccessLevels
                            users={selectedUsers}
                            participantLevels={participantLevels}
                            setParticipantLevel={handleChangeParticipantLevel} />
                    </div>
                </Wizard.StepContent>
            </Wizard.Step>
            <Wizard.Footer>
                <div className="d-flex justify-content-between">
                    <Button.Cancel
                        disabled={saving}
                        onClick={handleCancel} />
                    <div className="d-flex">
                        {currWizardStep > STEPS.USERS &&
                            <Button.Prev
                                disabled={saving}
                                className="mr-50"
                                onClick={() => goToStep(currWizardStep - 1)} />
                        }
                        {currWizardStep === STEPS.PERMISSIONS ?
                            <Button.Confirm
                                disabled={saving}
                                loading={saving}
                                dark
                                onClick={handleConfirm} />
                            :
                            <Button.Next
                                dark
                                onClick={() => goToStep(currWizardStep + 1)} />
                        }
                    </div>
                </div>
            </Wizard.Footer>
        </Wizard>
    );
};

export default AddGroupParticipantsModal;
